import * as React from 'react';
import {
  cleanup,
  EVENT_ON_KEY_DOWN,
  EVENT_ON_WHEEL,
  useController,
  useDocumentContext,
  useEventCallback,
  useLightboxState,
} from 'yet-another-react-lightbox';
import { usePointerEvents } from '../../hooks/usePointerEvents';
import { useZoomProps } from './useZoomProps';
import type { useZoomState } from './useZoomState';

function distance(pointerA: React.MouseEvent, pointerB: React.MouseEvent) {
  return Math.hypot(pointerA.clientX - pointerB.clientX, pointerA.clientY - pointerB.clientY);
}

function scaleZoom(value: number, delta: number, factor = 100, clamp = 2) {
  return value * Math.min(1 + Math.abs(delta / factor), clamp) ** Math.sign(delta);
}

export function useZoomSensors(
  zoom: number,
  minZoom: number,
  maxZoom: number,
  disabled: boolean,
  zoomIn: ReturnType<typeof useZoomState>['zoomIn'],
  zoomOut: ReturnType<typeof useZoomState>['zoomOut'],
  changeZoom: ReturnType<typeof useZoomState>['changeZoom'],
  changeOffsets: ReturnType<typeof useZoomState>['changeOffsets'],
  zoomWrapperRef?: React.RefObject<HTMLDivElement | null>
) {
  const activePointers = React.useRef<React.PointerEvent[]>([]);
  const lastPointerDown = React.useRef(0);
  const pinchZoom = React.useRef<{
    previousDistance: number;
    initialDistance: number;
    initialZoom: number;
  }>(undefined);

  const { globalIndex } = useLightboxState();
  const { getOwnerWindow } = useDocumentContext();
  const { containerRef, subscribeSensors } = useController();
  const {
    keyboardMoveDistance,
    zoomInMultiplier,
    wheelZoomDistanceFactor,
    scrollToZoom,
    doubleTapDelay,
    doubleClickDelay,
    doubleClickMaxStops,
    pinchZoomDistanceFactor,
    pinchZoomV4,
    wheelZoomStepRatio,
  } = useZoomProps();

  const translateCoordinates = React.useCallback(
    (event: React.MouseEvent) => {
      if (containerRef.current) {
        const { pageX, pageY } = event;
        const { scrollX, scrollY } = getOwnerWindow();
        const { left, top, width, height } = containerRef.current.getBoundingClientRect();
        return [pageX - left - scrollX - width / 2, pageY - top - scrollY - height / 2];
      }
      return [];
    },
    [containerRef, getOwnerWindow]
  );

  const onKeyDown = useEventCallback((event: React.KeyboardEvent) => {
    const { key, metaKey, ctrlKey } = event;
    const meta = metaKey || ctrlKey;

    const preventDefault = () => {
      event.preventDefault();
      event.stopPropagation();
    };

    const move = (deltaX: number, deltaY: number) => {
      preventDefault();
      changeOffsets(deltaX, deltaY);
    };

    if (key === 'ArrowDown') {
      return;
      move(0, keyboardMoveDistance);
    } else if (key === 'ArrowUp') {
      return;
      move(0, -keyboardMoveDistance);
    } else if (key === 'ArrowLeft') {
      return;
      move(-keyboardMoveDistance, 0);
    } else if (key === 'ArrowRight') {
      return;
      move(keyboardMoveDistance, 0);
    }

    if (key === '+' || (meta && key === '=')) {
      preventDefault();
      zoomIn();
    } else if (key === '-' || (meta && key === '_')) {
      preventDefault();
      zoomOut();
    } else if (meta && key === '0') {
      preventDefault();
      changeZoom(1);
    }
  });

  const onWheel = useEventCallback((event: React.WheelEvent) => {
    if (event.ctrlKey || scrollToZoom) {
      if (Math.abs(event.deltaY) > Math.abs(event.deltaX)) {
        event.stopPropagation();
        changeZoom(
          scaleZoom(zoom, -event.deltaY * wheelZoomStepRatio, wheelZoomDistanceFactor),
          true,
          ...translateCoordinates(event)
        );

        return;
      }
    }

    event.stopPropagation();

    if (!scrollToZoom) {
      changeOffsets(event.deltaX, event.deltaY);
    }
  });

  const clearPointer = React.useCallback((event: React.PointerEvent) => {
    const pointers = activePointers.current;
    pointers.splice(
      0,
      pointers.length,
      ...pointers.filter((p) => p.pointerId !== event.pointerId)
    );
  }, []);

  const replacePointer = React.useCallback(
    (event: React.PointerEvent) => {
      clearPointer(event);
      event.persist();
      activePointers.current.push(event);
    },
    [clearPointer]
  );

  const onPointerDown = useEventCallback((event: React.PointerEvent) => {
    const pointers = activePointers.current;

    if (
      // ignore right button clicks (e.g., context menu)
      (event.pointerType === 'mouse' && event.buttons > 1) ||
      // ignore clicks outside current slide (zoom icons, navigation buttons, etc.)
      !zoomWrapperRef?.current?.contains(event.target as unknown as Element)
    ) {
      return;
    }

    // click container to close
    if (event.target === zoomWrapperRef?.current) {
      return;
    }

    event.stopPropagation();

    const { timeStamp } = event;
    if (
      pointers.length === 0 &&
      timeStamp - lastPointerDown.current <
        (event.pointerType === 'touch' ? doubleTapDelay : doubleClickDelay)
    ) {
      lastPointerDown.current = 0;

      const targetZoom =
        zoom >= 1
          ? zoom !== maxZoom
            ? zoom * Math.max(maxZoom ** (1 / doubleClickMaxStops), zoomInMultiplier)
            : 1
          : zoom !== minZoom
            ? zoom / Math.max(minZoom ** (-1 / doubleClickMaxStops), zoomInMultiplier)
            : 1;

      changeZoom(targetZoom, false, ...translateCoordinates(event));
    } else {
      lastPointerDown.current = timeStamp;
    }

    replacePointer(event);

    if (pointers.length === 2) {
      const initialDistance = distance(pointers[0], pointers[1]);
      pinchZoom.current = {
        previousDistance: initialDistance,
        initialDistance: Math.max(initialDistance, 1), // prevent potential division by zero
        initialZoom: zoom,
      };
    }
  });

  const onPointerMove = useEventCallback((event: React.PointerEvent) => {
    const pointers = activePointers.current;

    const activePointer = pointers.find((p) => p.pointerId === event.pointerId);

    if (pointers.length === 2 && pinchZoom.current) {
      event.stopPropagation();

      replacePointer(event);

      const currentDistance = distance(pointers[0], pointers[1]);

      // TODO v4: remove `pinchZoomV4` flag and make this the default behavior
      const targetZoom = pinchZoomV4
        ? (pinchZoom.current.initialZoom / pinchZoom.current.initialDistance) * currentDistance
        : scaleZoom(
            zoom,
            currentDistance - pinchZoom.current.previousDistance,
            pinchZoomDistanceFactor
          );

      changeZoom(
        targetZoom,
        true,
        ...pointers
          .map((x) => translateCoordinates(x))
          .reduce((acc, coordinate) => coordinate.map((x, i) => acc[i] + x / 2))
      );

      pinchZoom.current.previousDistance = currentDistance;

      return;
    }

    event.stopPropagation();

    if (activePointer) {
      if (pointers.length === 1) {
        changeOffsets(
          (activePointer.clientX - event.clientX) / zoom,
          (activePointer.clientY - event.clientY) / zoom
        );
      }

      replacePointer(event);
    }
  });

  const onPointerUp = React.useCallback(
    (event: React.PointerEvent) => {
      const pointers = activePointers.current;

      if (pointers.length === 2 && pointers.find((p) => p.pointerId === event.pointerId)) {
        pinchZoom.current = undefined;
      }

      clearPointer(event);
    },
    [clearPointer]
  );

  const cleanupSensors = React.useCallback(() => {
    const pointers = activePointers.current;
    pointers.splice(0, pointers.length);

    lastPointerDown.current = 0;
    pinchZoom.current = undefined;
  }, []);

  usePointerEvents(subscribeSensors, onPointerDown, onPointerMove, onPointerUp, disabled);

  React.useEffect(cleanupSensors, [globalIndex, cleanupSensors]);

  React.useEffect(() => {
    if (!disabled) {
      return cleanup(
        cleanupSensors,
        subscribeSensors(EVENT_ON_KEY_DOWN, onKeyDown),
        subscribeSensors(EVENT_ON_WHEEL, onWheel)
      );
    }
    return () => {};
  }, [disabled, subscribeSensors, cleanupSensors, onKeyDown, onWheel]);
}
